/**
 * Type mappers for converting between BE step types and FE node types
 */

// BE step.type -> FE node.type
// Multiple BE start step types all map to "startNode" (entity-specific, distinguished by formData.beType)
const BE_TO_FE_TYPE_MAP: Record<string, string> = {
    integration: "startNode",
    incoming_postback: "startNode",
    outgoing_postback: "startNode",
    validation: "startNode",
    structure: "startNode",
    http: "httpNode",
    set: "setNode",
    modify: "modifyFieldNode",
    success: "endNode",
    delay: "waitNode",
    repeater: "repeaterNode",
    breaker: "breakerNode",
    mirror: "mirrorNode",
    scenario: "subScenarioNode",
    webhook: "webhookNode",
    condition: "conditionNode",
};

// FE node.type -> BE step.type (reverse mapping, for non-start nodes only)
// Note: startNode is NOT included here — it uses formData.beType for entity-specific BE type
const FE_TO_BE_TYPE_MAP: Record<string, string> = {
    httpNode: "http",
    setNode: "set",
    modifyFieldNode: "modify",
    endNode: "success",
    waitNode: "delay",
    repeaterNode: "repeater",
    breakerNode: "breaker",
    mirrorNode: "mirror",
    subScenarioNode: "scenario",
    webhookNode: "webhook",
    conditionNode: "condition",
};

/**
 * Convert BE step type to FE node type
 */
export function mapBeTypeToFeType(beType: string): string {
    return BE_TO_FE_TYPE_MAP[beType] || beType;
}

/**
 * Convert FE node type to BE step type
 */
export function mapFeTypeToBeType(feType: string): string {
    return FE_TO_BE_TYPE_MAP[feType] || feType.replace("Node", "");
}

// Flow type mapping: BE meaning -> FE flow
const MEANING_TO_FLOW_MAP: Record<string, string> = {
    check: "ping",
    send: "post",
    postsend_verification: "postVerify",
    webhook: "webhook",
    instant: "instant",
};

// Reverse: FE flow -> BE meaning
const FLOW_TO_MEANING_MAP: Record<string, string> = {
    ping: "check",
    post: "send",
    postVerify: "postsend_verification",
    webhook: "webhook",
    instant: "instant",
};

/**
 * Convert BE meaning to FE flow type (for Start node)
 */
export function mapMeaningToFlow(meaning: string): string {
    return MEANING_TO_FLOW_MAP[meaning] || meaning;
}

/**
 * Convert FE flow type to BE meaning (for Start node)
 */
export function mapFlowToMeaning(flow: string): string {
    return FLOW_TO_MEANING_MAP[flow] || flow;
}

// Connection validator mapping: BE validator -> FE operator
const VALIDATOR_TO_OPERATOR_MAP: Record<string, string> = {
    equals: "equals",
    not_equals: "notEquals",
    contains: "contains",
    not_contains: "notContains",
    starts_with: "startsWith",
    ends_with: "endsWith",
    greater_than: "greaterThan",
    less_than: "lessThan",
    is_empty: "isEmpty",
    is_not_empty: "isNotEmpty",
};

// Reverse mapping
const OPERATOR_TO_VALIDATOR_MAP: Record<string, string> = Object.fromEntries(
    Object.entries(VALIDATOR_TO_OPERATOR_MAP).map(([validator, operator]) => [operator, validator])
);

/**
 * Convert BE validator to FE operator (for edge conditions)
 */
export function mapValidatorToOperator(validator: string): string {
    return VALIDATOR_TO_OPERATOR_MAP[validator] || validator;
}

/**
 * Convert FE operator to BE validator (for edge conditions)
 */
export function mapOperatorToValidator(operator: string): string {
    return OPERATOR_TO_VALIDATOR_MAP[operator] || operator;
}
